/**
 * The Drift deck — one global event card per round.
 *
 * Events do two jobs. Some fire once, immediately, when the card flips
 * (`applyEventImmediate`). Others are *modifiers* the rest of the engine asks about
 * while the card is face up (`eventProductionBonus`, `eventJumpModifier`,
 * `eventTradeRatio`). Keeping modifiers as pure queries over `state.currentEvent` means
 * we never have to store per-round buff state and never have to remember to clear it.
 */

import { REGIONS, TRADE_GIVE, TRADE_TAKE } from './constants';
import type { Rng } from './rng';
import {
  allSystems, countOwnedOfClass, fleetsOf, isBlocked, playerOf, systemsOwnedBy,
} from './queries';
import type {
  EventId, GameEventCard, GameState, NoticeSink, PlayerId, Resources,
} from './types';

export const EVENTS: Readonly<Record<EventId, GameEventCard>> = Object.freeze({
  solarFlare: {
    id: 'solarFlare', name: 'Solar Flare', tone: 'bad',
    blurb: 'Radiation floods the belt. Volcanic and Anomaly systems produce nothing this round.',
  },
  oreRush: {
    id: 'oreRush', name: 'Ore Rush', tone: 'good',
    blurb: 'Assay reports go wide. +1 Ore for every Volcanic or Asteroid system you hold.',
  },
  bumperHarvest: {
    id: 'bumperHarvest', name: 'Bumper Harvest', tone: 'good',
    blurb: 'A good year everywhere. Every charter gains 2 Biomass.',
  },
  ionStorm: {
    id: 'ionStorm', name: 'Ion Storm', tone: 'bad',
    blurb: 'A storm front blacks out three systems for the following round.',
  },
  dataLeak: {
    id: 'dataLeak', name: 'Data Leak', tone: 'mixed',
    blurb: 'The leader\'s archives spill. Leader loses 2 Data; everyone else gains 1.',
  },
  refugeeConvoy: {
    id: 'refugeeConvoy', name: 'Refugee Convoy', tone: 'mixed',
    blurb: 'Ships arrive with nowhere to go. +1 Population, and this round\'s upkeep is +1.',
  },
  quantumBloom: {
    id: 'quantumBloom', name: 'Quantum Bloom', tone: 'good',
    blurb: 'The anomalies sing. +2 Data for every Anomaly system you hold.',
  },
  pirateRaid: {
    id: 'pirateRaid', name: 'Pirate Raid', tone: 'bad',
    blurb: 'Raiders skim the lanes. Lose 1 Fuel, and any charter with 4+ fleets loses one.',
  },
  coreSurge: {
    id: 'coreSurge', name: 'Core Surge', tone: 'mixed',
    blurb: 'The galactic core flares bright. Core systems produce double this round.',
  },
  graviticShear: {
    id: 'graviticShear', name: 'Gravitic Shear', tone: 'bad',
    blurb: 'Space folds badly. All fleets have 1 less jump range this round.',
  },
  openMarkets: {
    id: 'openMarkets', name: 'Open Markets', tone: 'good',
    blurb: 'The exchanges deregulate. Trade is 2-for-2 this round instead of 3-for-2.',
  },
  cometHarvest: {
    id: 'cometHarvest', name: 'Comet Harvest', tone: 'good',
    blurb: 'A long-period comet swings through. Every charter gains 2 Fuel and 1 Ore.',
  },
});

export const ALL_EVENT_IDS: readonly EventId[] = Object.keys(EVENTS) as EventId[];

export function eventCard(id: EventId): GameEventCard {
  return EVENTS[id];
}

/* ---------------------------------------------------------------- immediate */

/**
 * Fire the one-shot part of an event, at the moment the card flips.
 *
 * Mutates `state`. Iterates seats in fixed order and uses `rng` only where the card
 * genuinely calls for a random target, so replays stay deterministic.
 */
export function applyEventImmediate(
  state: GameState,
  id: EventId,
  rng: Rng,
  notice?: NoticeSink,
): void {
  switch (id) {
    case 'solarFlare': {
      // "this round": the window opens now and closes before the next round.
      for (const sys of allSystems(state)) {
        if (sys.cls === 'volcanic' || sys.cls === 'anomaly') {
          sys.blockedFromRound = state.round;
          sys.blockedUntilRound = state.round + 1;
        }
      }
      break;
    }

    case 'ionStorm': {
      // Three random *owned or surveyed* systems go dark for the following round, so
      // the card always bites somebody rather than hitting empty space.
      const candidates = allSystems(state)
        .filter((s) => s.cls !== 'deepSpace' && (s.surveyed || s.owner !== null))
        .map((s) => s.id)
        .sort();
      for (let i = 0; i < 3 && candidates.length > 0; i++) {
        const pickIndex = rng.int(candidates.length);
        const pickedId = candidates.splice(pickIndex, 1)[0]!;
        const sys = state.systems[pickedId];
        if (sys) {
          // "the following round": the window opens next round, not this one. Drift precedes
          // Production in the same round, so opening it now would cost two rounds' output.
          sys.blockedFromRound = state.round + 1;
          sys.blockedUntilRound = state.round + 2;
        }
      }
      break;
    }

    case 'bumperHarvest':
      for (const p of state.players) p.resources.biomass += 2;
      break;

    case 'cometHarvest':
      for (const p of state.players) {
        p.resources.fuel += 2;
        p.resources.ore += 1;
      }
      break;

    case 'dataLeak': {
      // "Leader" is by current cached VP, tiebroken by seat so it is never ambiguous.
      let leader: PlayerId = 0;
      for (const p of state.players) {
        if (p.vp > playerOf(state, leader).vp) leader = p.id;
      }
      for (const p of state.players) {
        if (p.id === leader) p.resources.data = Math.max(0, p.resources.data - 2);
        else p.resources.data += 1;
      }
      break;
    }

    case 'refugeeConvoy':
      for (const p of state.players) {
        p.population = Math.min(12 + (p.megastructure === 'ringworld' ? 1 : 0), p.population + 1);
      }
      break;

    case 'pirateRaid':
      for (const p of state.players) {
        p.resources.fuel = Math.max(0, p.resources.fuel - 1);
        const fleets = fleetsOf(state, p.id);
        if (fleets.length >= 4) {
          // Fleets are fungible, so *which* one dies has no mechanical effect — only replay
          // stability matters. Order by the numeric part of the id rather than the string:
          // a plain sort puts "f10" before "f2", which is deterministic but arbitrary enough
          // to be a trap for anyone reasoning about it later. Newest commission is lost.
          const seq = (id: string) => Number(id.slice(1)) || 0;
          const doomed = fleets.reduce((a, b) => (seq(b.id) > seq(a.id) ? b : a));
          delete state.fleets[doomed.id];
        }
      }
      break;

    // Pure modifiers — nothing happens at flip time.
    case 'oreRush':
    case 'quantumBloom':
    case 'coreSurge':
    case 'graviticShear':
    case 'openMarkets':
      break;
  }

  notice?.({ type: 'eventCard', eventId: id });
}

/* ---------------------------------------------------------------- modifiers */

/**
 * Extra production granted by the face-up event, for one player.
 * Added on top of `productionFor`.
 */
export function eventProductionBonus(state: GameState, seat: PlayerId): Resources {
  const bonus: Resources = { ore: 0, fuel: 0, biomass: 0, data: 0 };
  const id = state.currentEvent;
  if (!id) return bonus;

  switch (id) {
    case 'oreRush':
      bonus.ore +=
        countOwnedOfClass(state, seat, 'volcanic') + countOwnedOfClass(state, seat, 'asteroid');
      break;

    case 'quantumBloom':
      bonus.data += countOwnedOfClass(state, seat, 'anomaly') * 2;
      break;

    case 'coreSurge': {
      // Core systems pay out a second time. Colonies already double, so this stacks
      // with the development multiplier rather than replacing it.
      for (const sys of systemsOwnedBy(state, seat)) {
        if (sys.region !== 'core') continue;
        if (isBlocked(sys, state.round)) continue;
        const multiplier = sys.development === 'colony' ? 2 : 1;
        bonus.ore += sys.yield.ore * multiplier;
        bonus.fuel += sys.yield.fuel * multiplier;
        bonus.biomass += sys.yield.biomass * multiplier;
        bonus.data += sys.yield.data * multiplier;
      }
      break;
    }

    default:
      break;
  }
  return bonus;
}

/** Jump-range delta from the face-up event. */
export function eventJumpModifier(state: GameState): number {
  return state.currentEvent === 'graviticShear' ? -1 : 0;
}

/** Trade ratio while the event is face up: `[give, take]`. */
export function eventTradeRatio(state: GameState): [number, number] {
  if (state.currentEvent === 'openMarkets') return [2, TRADE_TAKE];
  return [TRADE_GIVE, TRADE_TAKE];
}

/** Extra biomass owed at upkeep because of the event. */
export function eventUpkeepSurcharge(state: GameState): number {
  return state.currentEvent === 'refugeeConvoy' ? 1 : 0;
}

/** A one-line summary of the live event, for the HUD ticker. */
export function eventSummary(state: GameState): string {
  const id = state.currentEvent;
  if (!id) return 'No drift event.';
  const card = EVENTS[id];
  return `${card.name} — ${card.blurb}`;
}

/** Region tint used when an event highlights a region (Core Surge). */
export function eventHighlightTint(state: GameState): string | null {
  return state.currentEvent === 'coreSurge' ? REGIONS.core.tint : null;
}
